import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Trans, useTranslation } from 'react-i18next';
import { Button, FlatButton, Spacing, Text } from '@reservamos/elements';
import ModalWithTheme from 'components/ModalWithTheme';

/**
 * DenialPromptModal component.
 * Render the first modal asking the user whether to continue browsing or
 * proceed to issue a "Negativa de Concessão de Benefício".
 *
 * @param {object} props - date, onClose, onContinueShopping, onIssueDenial.
 * @param {string} props.date - Selected travel date in YYYY-MM-DD format.
 * @param {boolean} props.halfFareAvailable - When true, shows the half-fare (50%) variant copy,
 *   letting the user continue the purchase at 50% instead of only issuing the denial.
 * @param {Function} props.onClose - Handler invoked when the modal X is clicked.
 * @param {Function} props.onContinueShopping - Handler for the "Continuar compra" CTA.
 * @param {Function} props.onIssueDenial - Handler that opens the denial form.
 * @returns {JSX.Element} The rendered modal.
 */
const DenialPromptModal = ({
  date,
  halfFareAvailable,
  onClose,
  onContinueShopping,
  onIssueDenial,
}) => {
  const { t } = useTranslation('search');
  const formattedDate = date ? moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';

  return (
    <ModalWithTheme
      onCloseModal={onClose}
      closeOnOverlayClick={false}
      rounded="M"
      responsiveSize="S"
    >
      <Spacing vertical size="L" alignItems="center">
        <Text textAlign="center">
          <Trans
            t={t}
            i18nKey={halfFareAvailable ? 'negativa.prompt.body_half_fare' : 'negativa.prompt.body'}
            values={{ date: formattedDate }}
            components={{ b: <strong /> }}
          />
        </Text>
        <Text textAlign="center">
          <Trans t={t} i18nKey="negativa.prompt.instructions" components={{ b: <strong /> }} />
        </Text>
        <Text textAlign="center" weight="bold">
          {t('negativa.prompt.question')}
        </Text>
        <Spacing size="S" justifyContent="center" alignItems="center">
          <FlatButton onClick={onIssueDenial}>{t('negativa.prompt.cta_negativa')}</FlatButton>
          <Button
            variant="primary"
            text={t('negativa.prompt.cta_continue')}
            onClick={onContinueShopping}
          />
        </Spacing>
      </Spacing>
    </ModalWithTheme>
  );
};

DenialPromptModal.propTypes = {
  date: PropTypes.string,
  halfFareAvailable: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onContinueShopping: PropTypes.func.isRequired,
  onIssueDenial: PropTypes.func.isRequired,
};

export default DenialPromptModal;
